import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users } from "lucide-react";

const HUB = [
  { v: "anak", l: "Anak Kandung" },
  { v: "anak_angkat", l: "Anak Angkat" },
  { v: "orang_tua", l: "Orang Tua" },
  { v: "mertua", l: "Mertua" },
  { v: "lainnya", l: "Lainnya" },
];
const hl = (v) => HUB.find((x) => x.v === v)?.l || v || "-";

export default function TanggunganPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", nik: "", hubungan: "anak", tahun_lahir: "", keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/tanggungan"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.nama?.trim()) { toast.error("Nama tanggungan wajib diisi"); return; }
    const payload = { ...form, tahun_lahir: Number(form.tahun_lahir) || null };
    try {
      if (editId) await api.put(`/tanggungan/${editId}`, payload);
      else await api.post("/tanggungan", payload);
      toast.success(editId ? "Diperbarui" : "Tanggungan tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOne = async (id) => { if (!confirm("Hapus tanggungan ini?")) return; await api.delete(`/tanggungan/${id}`); toast.success("Terhapus"); load(); };

  return (
    <>
      <PageHeader title="Daftar Tanggungan (L-1 C)" subtitle="Anggota keluarga yang menjadi tanggungan (maks 3 memengaruhi PTKP)." actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-tanggungan-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 flex items-center gap-3 text-sm text-slate-600">
          <Users size={18} className="text-blue-600" /> Jumlah tanggungan tercatat: <b>{items.length}</b>. Sesuaikan status PTKP di menu <b>Pengaturan Pajak</b>.
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="tanggungan-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Nama</th><th className="p-3">Hubungan</th><th className="p-3">NIK</th><th className="p-3">Tahun Lahir</th><th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-slate-400">Belum ada tanggungan.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">{p.nama}</td>
                  <td className="p-3">{hl(p.hubungan)}</td>
                  <td className="p-3 font-mono text-xs">{p.nik || "-"}</td>
                  <td className="p-3">{p.tahun_lahir || "-"}</td>
                  <td className="p-3"><div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-tanggungan-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-tanggungan-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tanggungan-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Tanggungan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nama <span className="text-rose-500">*</span></Label><Input value={form.nama} onChange={upd("nama")} data-testid="tanggungan-nama" /></div>
            <div className="space-y-1.5"><Label>Hubungan</Label>
              <Select value={form.hubungan} onValueChange={(v) => setForm((f) => ({ ...f, hubungan: v }))}>
                <SelectTrigger data-testid="tanggungan-hubungan"><SelectValue /></SelectTrigger>
                <SelectContent>{HUB.map((h) => <SelectItem key={h.v} value={h.v}>{h.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Tahun Lahir</Label><Input type="number" value={form.tahun_lahir || ""} onChange={upd("tahun_lahir")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>NIK</Label><Input value={form.nik} onChange={upd("nik")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="tanggungan-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
